import type { NotificationApiItem } from './types.js';
import { formatNumber } from './money.js';

/**
 * Composite key for one notification (MYP-66), built from the three fields the ticket names:
 * `${type}:${tid}:${msgdate}`. This is deliberately not `id` — the point of a composite key is to
 * identify the *same underlying message* across two overlapping fetches (e.g. two pages of
 * `get_msgs` that share a boundary row), which a page-local `id` can't do on its own. Used for
 * React list keys and for `dedupeNotifications` below.
 */
export function notificationKey(
  item: Pick<NotificationApiItem, 'type' | 'tid' | 'msgdate'>,
): string {
  return `${item.type}:${item.tid}:${item.msgdate}`;
}

/**
 * Collapses a list to one entry per composite key (`notificationKey`), keeping the first
 * occurrence — the list is assumed newest-first (see `fixtures.ts`'s own ordering note), so "first"
 * is also "most recently seen," which matters once real pagination can hand back the same message
 * on two adjacent pages.
 *
 * Pure and list-shaped on purpose: this is meant to run once between "notifications just fetched"
 * and "notifications the UI counts and renders," wherever that seam ends up living (today, that's
 * `NotificationsProvider`; once MYP-70 wires a real fetch, the same seam is where a new page gets
 * merged in).
 */
export function dedupeNotifications<
  T extends Pick<NotificationApiItem, 'type' | 'tid' | 'msgdate'>,
>(items: readonly T[]): T[] {
  const seen = new Set<string>();
  const result: T[] = [];

  for (const item of items) {
    const key = notificationKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(item);
  }

  return result;
}

/**
 * The order/tracking number or account number a customer would recognise this message by
 * (MYP-112) — `orderno` when the type carries one (PACKAGE, ORDER, AB), otherwise `tid` itself,
 * which is at least something stable and type-appropriate to show even when it isn't a number a
 * customer chose (an account number for AUTH, an internal message id for ALIPAY/COMPEN — see
 * `NotificationApiItem`'s doc comment).
 */
export function notificationReference(item: Pick<NotificationApiItem, 'tid' | 'orderno'>): string {
  return item.orderno ?? item.tid;
}

/**
 * The types that still have an unread message, in first-seen order — one `set_msgs_read` call per
 * type is how "mark all as read" is sent (MYP-70): the endpoint marks every unread message of a
 * type in one go, and has no "everything" form.
 */
export function unreadTypes(
  items: readonly Pick<NotificationApiItem, 'type' | 'readflag'>[],
): string[] {
  const types: string[] = [];
  for (const item of items) {
    if (item.readflag === 'N' && !types.includes(item.type)) types.push(item.type);
  }
  return types;
}

/** Above this many unread, the badge stops counting exactly and just says "many." */
const UNREAD_BADGE_CAP = 9;

/**
 * The header bell and sidebar badges are small fixed-width numerals, not prose — past a point, an
 * exact count stops being legible or useful ("47" is not meaningfully different from "the tray is
 * full" at a glance) and just makes the badge wider than its neighbours. `9+` is deliberately not
 * cased through `formatNumber`: it isn't a number in the target locale, it's a fixed ASCII glyph
 * the design uses in every language.
 *
 * Deliberately not applied everywhere a count appears — the Notifications page's own "14 unread"
 * sentence is prose, not a small badge, and capping it there would just discard information for no
 * layout reason. Callers decide per call site; this only formats the numeral badges that ask for it.
 */
export function formatUnreadBadge(count: number, locale: string): string {
  return count > UNREAD_BADGE_CAP ? `${UNREAD_BADGE_CAP}+` : formatNumber(count, locale);
}
